import { useIntl } from 'react-intl';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { AccordionDetails, AccordionSummary, Box } from '@material-ui/core';
import React, { FC } from 'react';
import { AboutPageTab } from '../../../containers/AboutPage/aboutPageUtils';
import { useShopConfig } from '../../../hooks/shopConfig';
import { NamedLink, StyledAccordion, TypographyWrapper } from '../..';
import { FAQ } from '../../../types/shopConfig/shopConfigV2';
import { useEnabledCustomerExperiences } from '../../../hooks/useEnabledCustomerExperiences';
import { useReturnInsurance } from '../../../hooks/useReturnInsurance';
import { SEEL_RETURN_ASSURANCE } from '../../../util/seelHelpers';
import { formatDollarAmountAsCurrency } from '../../../util/currency';
import { doesShopAllowBrandDirectReturns } from '../../../util/returns';
import { TypographyFormat } from '../../TypographyWrapper/TypographyWrapper';
import { FAQDescription, FAQTitle } from '../components/FAQComponents';

export const RefundPolicyFAQAccordionSection: FC = () => {
  const {
    treetShopName,
    copy: { faqCopy },
    internationalConfig,
    returnShippingPrice,
  } = useShopConfig();
  const intl = useIntl();

  const { allowSell } = useEnabledCustomerExperiences();
  const { shouldOfferReturnInsurance } = useReturnInsurance();

  const shopAllowsBrandDirectReturns = doesShopAllowBrandDirectReturns(
    internationalConfig,
    returnShippingPrice
  );

  const treetProtectionText = !allowSell
    ? `All ${treetShopName} orders are final sale; however, all orders`
    : `All other orders made through ${treetShopName}`;

  return (
    <StyledAccordion name="return-refund-policy">
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel4a-content"
        id="panel4a-header"
      >
        <h4>
          <FAQTitle>{faqCopy[FAQ.Refund]?.title || 'What’s the refund/return policy?'}</FAQTitle>
        </h4>
      </AccordionSummary>
      <AccordionDetails>
        {faqCopy[FAQ.Refund] ? (
          <span>
            <FAQDescription>{faqCopy[FAQ.Refund].description}</FAQDescription>
          </span>
        ) : (
          <div>
            {shopAllowsBrandDirectReturns && (
              <TypographyWrapper variant="body1">
                {`All new with tags Brand Direct orders made through ${treetShopName} have a 14 day return policy. Items must be in an unused, unaltered condition and returned with their tags and packaging.
                        The original shipping fee will not be returned and a ${formatDollarAmountAsCurrency(
                          intl,
                          returnShippingPrice.price,
                          returnShippingPrice.currency
                        )} return shipping fee will be deducted from your final refund.`}
              </TypographyWrapper>
            )}
            <Box mt={1}>
              <TypographyWrapper variant="body1">
                {treetProtectionText} are backed by{' '}
                <NamedLink name="AboutPage" params={{ tab: AboutPageTab.Protection }}>
                  <TypographyWrapper
                    variant="body1"
                    typographyOverrides={{ display: 'inline' }}
                    format={TypographyFormat.Underlined}
                  >
                    Treet Protection
                  </TypographyWrapper>
                </NamedLink>
                : get the item that was promised in the condition it was promised in, or receive a
                full refund.
              </TypographyWrapper>
            </Box>
            <Box mt={1}>
              <TypographyWrapper variant="body1">
                In order to receive a refund for your order, you must file a Misrepresentation Claim
                within 72 hours of receiving your order.{' '}
                <NamedLink name="AboutPage" params={{ tab: AboutPageTab.Protection }}>
                  <TypographyWrapper
                    variant="body1"
                    typographyOverrides={{
                      display: 'inline',
                    }}
                    format={TypographyFormat.Underlined}
                  >
                    Here
                  </TypographyWrapper>
                </NamedLink>{' '}
                are the steps to do so.
              </TypographyWrapper>
            </Box>
            {allowSell && (
              <Box mt={1}>
                <TypographyWrapper variant="body1">
                  If for whatever reason you decide you don’t like your item, this is not covered in
                  Treet Protection, but you still can re-list the product without paying any Seller
                  fees. In this case, you must request to re-list the item within 72 hours of its
                  arrival by contacting support@treet.co.
                </TypographyWrapper>
              </Box>
            )}
            {shouldOfferReturnInsurance && (
              <Box mt={1}>
                <TypographyWrapper variant="body1">
                  You can also choose to add Seel {SEEL_RETURN_ASSURANCE} to your order at checkout
                  if your order is eligible. {SEEL_RETURN_ASSURANCE} gives you a 7-day return window
                  on your otherwise final sale (non-refundable) items. If you’re unhappy with the
                  purchase for any reason, Seel will buy it back from you for 100% of the purchase
                  price of your returned items. Please note that shipping fees are not covered by{' '}
                  {SEEL_RETURN_ASSURANCE}. The original shipping fee will not be returned and a $10
                  return shipping fee will be deducted from your final refund.
                </TypographyWrapper>
              </Box>
            )}
          </div>
        )}
      </AccordionDetails>
    </StyledAccordion>
  );
};
